import * as tf from '@tensorflow/tfjs'

const MOBILE_NET_INPUT_WIDTH = 224
const MOBILE_NET_INPUT_HEIGHT = 224

const l2normalize = (vec) => {
    let sum = 0
    for (const x of vec) sum += x * x
    const norm = Math.sqrt(sum) || 1
    return vec.map((x) => x / norm)
}

// Few-shot KNN training: embed every sample with the frozen MobileNet feature
// extractor, then store one L2-normalized centroid per class on the model.
// Classification at runtime is cosine similarity against these centroids, so
// there is no training loop and no saved TF model.
export const trainKnnModel = async ({
    modelData,
    mobilenet,
    camModels,
    setModelDetails,
}) => {
    const embed = async (imageSrc) => {
        const img = new Image()
        img.src = imageSrc
        await new Promise((resolve) => {
            img.onload = resolve
        })
        const features = tf.tidy(() => {
            const t = tf.browser.fromPixels(img)
            const r = tf.image
                .resizeBilinear(
                    t,
                    [MOBILE_NET_INPUT_HEIGHT, MOBILE_NET_INPUT_WIDTH],
                    true
                )
                .div(255)
            return mobilenet.predict(r.expandDims()).squeeze()
        })
        const arr = await features.array()
        features.dispose()
        return l2normalize(arr)
    }

    const classes = modelData.ImageClasses || {}
    const centroids = []
    const CLASS_NAMES = []

    for (const key in classes) {
        const eachClass = classes[key]
        const images = eachClass.capturedImages || []
        if (!images.length) continue

        let sum = null
        let count = 0
        for (const src of images) {
            const norm = await embed(src)
            if (!sum) sum = new Array(norm.length).fill(0)
            for (let i = 0; i < norm.length; i++) sum[i] += norm[i]
            count++
        }
        if (!count) continue

        const mean = sum.map((x) => x / count)
        centroids.push({
            classId: eachClass.id,
            className: eachClass.className,
            vector: l2normalize(mean),
        })
        CLASS_NAMES.push(eachClass.className)
    }

    if (centroids.length < 2) {
        throw new Error('Need at least 2 classes with samples to train')
    }

    const updated = [...camModels]
    const index = updated.findIndex((m) => m.modelId === modelData.modelId)
    if (index !== -1) {
        updated[index].isGenerateTensor = true
        updated[index].classLabels = CLASS_NAMES
        updated[index].knnCentroids = centroids
    }
    setModelDetails(updated)
}
